import {z} from 'zod'
import {DateRangeFilterEnum} from '@/types'
import {ProjectProgressStatus} from '../types'

export const SiarBulkExportType = z.enum(['open_project', 'close_project'])
export type SiarBulkExportType = z.infer<typeof SiarBulkExportType>

export const SiarExportStatus = z.enum(['pending', 'partially_transmitted', 'transmitted'])
export type SiarExportStatus = z.infer<typeof SiarExportStatus>

export const SiarExportTransmissionStatus = z.enum(['pending', 'transmitted'])
export type SiarExportTransmissionStatus = z.infer<typeof SiarExportTransmissionStatus>

export const SiarExportProgressStatus = z.enum(['to_print', 'to_export', 'to_transmit'])
export type SiarExportProgressStatus = z.infer<typeof SiarExportProgressStatus>

// Preview `status` filter: the real progress statuses plus the dedicated "excluded" view
// that returns only non-exportable projects regardless of their progress status.
export const SiarPreviewStatusFilter = z.enum(['to_print', 'to_export', 'to_transmit', 'excluded'])
export type SiarPreviewStatusFilter = z.infer<typeof SiarPreviewStatusFilter>

export const SiarPreviewItemProgressStatus = ProjectProgressStatus.extract(['to_print', 'to_export', 'to_transmit'])
export type SiarPreviewItemProgressStatus = z.infer<typeof SiarPreviewItemProgressStatus>

// Reason a project is not exportable. Extensible: the BE may add new values over time,
// so this is parsed as a plain nullable string and the FE maps known values to labels
// with a generic fallback (see resolveExclusionReasonLabel in utils.ts).
export const KNOWN_EXCLUSION_REASONS = ['missing_initial_evaluation_access'] as const
export type KnownExclusionReason = (typeof KNOWN_EXCLUSION_REASONS)[number]

export const ExclusionReason = z.string().nullable()
export type ExclusionReason = z.infer<typeof ExclusionReason>

export const SiarExportCreatedBy = z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string()
})
export type SiarExportCreatedBy = z.infer<typeof SiarExportCreatedBy>

export const SiarExport = z.object({
    id: z.string(),
    fileName: z.string(),
    exportType: SiarBulkExportType,
    dateFrom: z.string().nullable(),
    dateTo: z.string().nullable(),
    status: SiarExportStatus,
    projectCount: z.number(),
    transmittedCount: z.number(),
    dailyProgressive: z.number(),
    addRegistry: z.boolean(),
    createdAt: z.string(),
    createdBy: SiarExportCreatedBy
})
export type SiarExport = z.infer<typeof SiarExport>

export const SiarExportPreviewItemInOtherBatch = z.object({
    id: z.string(),
    fileName: z.string(),
    dailyProgressive: z.number()
})
export type SiarExportPreviewItemInOtherBatch = z.infer<typeof SiarExportPreviewItemInOtherBatch>

export const SiarExportPreviewItem = z.object({
    projectId: z.string(),
    projectCode: z.number(),
    patientFullName: z.string(),
    startDate: z.string().nullable(),
    expirationDate: z.string().nullable(),
    endDate: z.string().nullable(),
    progressStatus: SiarPreviewItemProgressStatus,
    transmissionStatus: SiarExportTransmissionStatus,
    transmissionDate: z.string().nullable(),
    openingExportedAt: z.string().nullable(),
    openingTransmissionAt: z.string().nullable(),
    inOtherBatch: SiarExportPreviewItemInOtherBatch.nullable(),
    exclusionReason: ExclusionReason
})
export type SiarExportPreviewItem = z.infer<typeof SiarExportPreviewItem>

export const SiarExportProjectProgressStatus = ProjectProgressStatus.extract(['to_transmit', 'transmitted'])
export type SiarExportProjectProgressStatus = z.infer<typeof SiarExportProjectProgressStatus>

export const SiarExportProject = z.object({
    id: z.string(),
    projectId: z.string(),
    projectCode: z.number(),
    patientFullName: z.string(),
    startDate: z.string().nullable(),
    expirationDate: z.string().nullable(),
    endDate: z.string().nullable(),
    progressStatus: SiarExportProjectProgressStatus,
    transmissionStatus: SiarExportTransmissionStatus,
    transmittedAt: z.string().nullable()
})
export type SiarExportProject = z.infer<typeof SiarExportProject>

export const SiarExportDetail = SiarExport.extend({
    projects: z.array(SiarExportProject)
})
export type SiarExportDetail = z.infer<typeof SiarExportDetail>

export const SiarExportHistorySearchParams = z.object({
    exportType: SiarBulkExportType.optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    filters: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    range: DateRangeFilterEnum.optional(),
    projectNumber: z.string().trim().optional(),
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().positive().default(20)
})
export type SiarExportHistorySearchParams = z.infer<typeof SiarExportHistorySearchParams>

export const SiarExportsColumn = z.enum([
    'exportType',
    'period',
    'createdAtAndAuthor',
    'status',
    'transmittedAt',
    'name',
    'moreActions'
])
export type SiarExportsColumn = z.infer<typeof SiarExportsColumn>
